import { Link } from "react-router-dom";
import { ScanFace, PenLine, MessageCircleHeart } from "lucide-react";
import PublicHeader, { Footer } from "@/components/PublicHeader";
import PricingCards from "@/components/PricingCards";
import MuseCollage from "@/components/MuseCollage";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const STATS = [["6", "Proven post formats"], ["1 of 1", "Look locked every post"], ["3 free", "Posts to start"]];

const BENEFITS = [
  { icon: ScanFace, title: "Same face, every post", body: "Her Identity Lock opens every image and video prompt — skin, hair, build and never-rules, word for word." },
  { icon: PenLine, title: "Never stare at a blank caption", body: "Pick from three on-voice ideas, approve one, and get hooks, captions and hashtags written in her tone." },
  { icon: MessageCircleHeart, title: "Turn views into sales", body: "Every selling post ships with a comment keyword and a DM-ready message that ends on your offer link." },
];
const STEPS = [
  ["Build your muse", "Five short steps: look, rules, sets, voice and offers."],
  ["Approve the idea", "Three quotes in her voice. Edit, swap or write your own."],
  ["Copy, generate, post", "Paste prompts into your generator and post with the caption and CTA."],
];
const FAQ = [
  ["Does it make the images?", "No, it writes the prompts for the generator you already use."],
  ["Which generators?", "Higgsfield, Kie, Midjourney, Kling, Runway and more."],
  ["Can I cancel anytime?", "Yes."],
];

export default function Landing() {
  return (
    <div className="min-h-screen" data-testid="landing-page">
      <PublicHeader />
      <section className="max-w-6xl mx-auto px-5 md:px-10 pt-4 md:pt-10 grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div className="fade-up"><MuseCollage /></div>
        <div className="fade-up" style={{ animationDelay: "120ms" }}>
          <p className="eyebrow mb-5">For AI influencer creators</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl" data-testid="landing-hero-title">
            Your AI influencer's<br /><span className="accent-text">content team.</span>
          </h1>
          <p className="muted text-base md:text-lg mt-6 max-w-lg leading-relaxed" data-testid="landing-hero-subtitle">
            Lock her look once. Get scroll-stopping prompts, captions and DM-ready CTAs for every post — in minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-9">
            <Link to="/signup" className="btn-primary py-4 px-8" data-testid="landing-start-free-button">Start free — 3 posts on us</Link>
            <Link to="/example" className="btn-secondary py-4 px-8" data-testid="landing-see-example-button">See an example</Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 md:px-10 mt-14" data-testid="landing-stats-strip">
        <div className="panel grid grid-cols-3 divide-x divide-[#F0EAF7]">
          {STATS.map(([n, l]) => (
            <div key={l} className="p-5 md:p-8 text-center">
              <p className="font-display text-3xl md:text-5xl accent-text">{n}</p>
              <p className="text-[10px] md:text-[11px] font-bold uppercase tracking-[0.16em] muted mt-2">{l}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 md:px-10 mt-32 grid md:grid-cols-3 gap-5" data-testid="landing-benefits">
        {BENEFITS.map((b, i) => (
          <div key={b.title} className="panel p-7 fade-up" style={{ animationDelay: `${i * 100}ms` }}>
            <span className="w-12 h-12 rounded-full bg-[#F1E9FE] flex items-center justify-center mb-6"><b.icon className="text-[#9C4DDC]" strokeWidth={1.75} size={22} /></span>
            <h3 className="text-2xl">{b.title}</h3>
            <p className="muted text-sm mt-3 leading-relaxed">{b.body}</p>
          </div>
        ))}
      </section>

      <section className="max-w-6xl mx-auto px-5 md:px-10 mt-32" data-testid="landing-how-it-works">
        <p className="eyebrow mb-3">How it works</p>
        <h2 className="text-3xl md:text-4xl mb-12">Three steps to a finished post.</h2>
        <div className="grid md:grid-cols-3 gap-10">
          {STEPS.map(([t, d], i) => (
            <div key={t} className="border-t border-[#9C4DDC]/50 pt-6">
              <span className="font-display text-5xl accent-text">{i + 1}</span>
              <h3 className="text-xl mt-4">{t}</h3>
              <p className="muted text-sm mt-2">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 md:px-10 mt-32" id="pricing" data-testid="landing-pricing">
        <p className="eyebrow mb-3">Pricing</p>
        <h2 className="text-3xl md:text-4xl mb-12">Start free. Go Pro when she takes off.</h2>
        <PricingCards />
      </section>

      <section className="max-w-3xl mx-auto px-5 md:px-10 mt-32" data-testid="landing-faq">
        <p className="eyebrow mb-3">FAQ</p>
        <Accordion type="single" collapsible className="w-full">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={q} value={`q${i}`} className="border-[#F0EAF7]">
              <AccordionTrigger className="font-bold text-base text-left hover:no-underline" data-testid={`faq-question-${i}`}>{q}</AccordionTrigger>
              <AccordionContent className="muted text-sm">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
      <Footer />
    </div>
  );
}
